import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AngleRightIcon from 'common/customIcons/angle_right';
import MDButton from 'components/MDButton';
import GoogleSuggestion from 'components/googleMaps/GoogleSuggestion';
import validateDistributor from 'validations/distributor';
import { useDispatch, useSelector } from 'react-redux';
import {
  createDistributor,
  deleteDistributor,
  updateDistributor,
} from '../../redux/slices/distributor';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { openSnackbar } from '../../redux/slices/common';
import { isEmptyObject } from 'utilities/common';
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from '../../redux/slices/modalSlice';
import { stateList } from 'utilities/common';

const BASE_PATH = '/hyperwolf/distributors';

const initialFormData = {
  distributorName: '',
  licenseNumber: '',
  contactName: '',
  distributorBillingEmail: '',
  phoneNumber: '',
  status: 'Active',
  address: {
    address: '',
    city: '',
    state: '',
    zipCode: '',
  },
};

export default function AddDistributorForm() {
  const { distributorDetails = {}, isLoading = false } = useSelector(
    (state) => state.distributor
  );
  const { distributorId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const blazeVendorId = searchParams.get('blazeVendorId') || undefined;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [addressError, setAddressError] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Nested address fields: name like "address.city"
    if (name.startsWith('address.')) {
      const key = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [key]: value,
        },
      }));
      if (errors.address) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.address;
          return next;
        });
      }
      return;
    }

    // Restrict phone to digits and max 10 chars
    if (name === 'phoneNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { isValid, errors: validationErrors } = validateDistributor(formData);
    if (!isValid) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const payload = {
      distributorName: formData.distributorName,
      distributorLicenseNumber: formData.licenseNumber,
      distributorBillingEmail: formData.distributorBillingEmail || undefined,
      contactPersonName: formData.contactName || undefined,
      phoneNumber: formData.phoneNumber || undefined,
      address: {
        address: formData.address?.address || '',
        city: formData.address?.city || '',
        state: formData.address?.state || '',
        zipCode: formData.address?.zipCode || '',
      },
      status: formData.status === 'Active',
    };
    if (distributorId) {
      dispatch(updateDistributor({ data: payload, distributorId, blazeVendorId }))
        .unwrap()
        .then((res) => {
          showMessage(res?.message, 'success');
          navigate(BASE_PATH);
        })
        .catch((err) => showMessage(err?.message || 'Update failed', 'error'));
    } else {
      dispatch(createDistributor({ data: payload }))
        .unwrap()
        .then((res) => {
          showMessage(res?.message, 'success');
          navigate(BASE_PATH);
        })
        .catch((err) => showMessage(err?.message || 'Create failed', 'error'));
    }
  };

  const showMessage = (message, type) => {
    dispatch(
      openSnackbar({
        content: message,
        color: type === 'success' ? 'success' : 'error',
        icon: type === 'success' ? 'check' : 'warning',
      })
    );
  };

  const deleteHandler = (id) => {
    dispatch(setModalLoader(true));
    dispatch(deleteDistributor(id))
      .unwrap()
      .then((res) => {
        showMessage(res?.message, 'success');
        dispatch(closeCustomModalDialog());
        navigate(BASE_PATH);
      })
      .catch((err) => {
        dispatch(setModalLoader(false));
        showMessage(err?.message || 'Delete failed', 'error');
      });
  };

  useEffect(() => {
    if (!isEmptyObject(distributorDetails)) {
      const details = distributorDetails;
      setFormData({
        distributorName: details.distributorName || details.name || '',
        licenseNumber: details.distributorLicenseNumber || details.licenseNumber || '',
        contactName: details.contactPersonName || details.contactName || '',
        distributorBillingEmail: details.distributorBillingEmail || details.email || '',
        phoneNumber: details.phoneNumber || '',
        status:
          details.status === true || details.status === 'active'
            ? 'Active'
            : details.status === false || details.status === 'inactive'
            ? 'Inactive'
            : 'Active',
        address: {
          address:
            (details.address && typeof details.address === 'object'
              ? details.address.address
              : details.address) || '',
          city:
            (details.address && typeof details.address === 'object'
              ? details.address.city
              : details.city) || '',
          state:
            (details.address && typeof details.address === 'object'
              ? details.address.state
              : details.state) || '',
          zipCode:
            (details.address && typeof details.address === 'object'
              ? details.address.zipCode || details.address.pincode || details.address.postalCode
              : details.postalCode) || '',
        },
      });
    }
  }, [distributorDetails]);

  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }}>
      <Box display="flex" alignItems="center" color="text.main" lineHeight={1} mb={2}>
        <IconButton onClick={() => navigate(BASE_PATH)}>
          <AngleRightIcon fontSize="14px" />
        </IconButton>
        <Typography variant="h6" fontWeight="semibold" ml={0.75}>
          Distributors
        </Typography>
      </Box>

      <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2.5, boxShadow: 'none' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          mb={4}
        >
          <Typography variant="h5" fontWeight="semibold" color="text.primary">
            {distributorId ? 'Edit' : 'Add'} Distributor
          </Typography>

          <Box display="flex" alignItems="center" gap={1.25}>
            <Typography variant="h6" fontWeight="semibold" color="secondary">
              Status
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              p={0.75}
              borderRadius={2}
              bgcolor="#F2F3F7"
            >
              <MDButton
                sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: 0.25, fontWeight: 500 }}
                type="button"
                variant={formData.status === 'Active' ? 'contained' : 'text'}
                color={formData.status === 'Active' ? 'success' : 'dark'}
                // size="small"
                onClick={() => setFormData((p) => ({ ...p, status: 'Active' }))}
              >
                Active
              </MDButton>
              <MDButton
                sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: 0.25, fontWeight: 500 }}
                type="button"
                variant={formData.status === 'Inactive' ? 'contained' : 'text'}
                color={formData.status === 'Inactive' ? 'error' : 'dark'}
                // size="small"
                onClick={() => setFormData((p) => ({ ...p, status: 'Inactive' }))}
              >
                Inactive
              </MDButton>
            </Stack>
          </Box>
        </Stack>

        {/* Row 1: Two required fields only */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Distributor Name*"
              fullWidth
              // size="small"
              variant="outlined"
              name="distributorName"
              value={formData.distributorName}
              onChange={handleInputChange}
              error={Boolean(errors.distributorName)}
              helperText={errors.distributorName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Distributor License Number*"
              fullWidth
              // size="small"
              variant="outlined"
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={handleInputChange}
              error={Boolean(errors.licenseNumber)}
              helperText={errors.licenseNumber}
            />
          </Grid>
        </Grid>
        {/* Row 2: Three optional fields */}
        <Grid container spacing={2.5} mb={4}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Contact Name"
              fullWidth
              // size="small"
              variant="outlined"
              name="contactName"
              value={formData.contactName}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Distributor Billing Email"
              fullWidth
              // size="small"
              variant="outlined"
              name="distributorBillingEmail"
              type="email"
              value={formData.distributorBillingEmail}
              onChange={handleInputChange}
              error={Boolean(errors.distributorBillingEmail)}
              helperText={errors.distributorBillingEmail}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Phone Number"
              fullWidth
              // size="small"
              variant="outlined"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              error={Boolean(errors.phoneNumber)}
              helperText={errors.phoneNumber}
            />
          </Grid>
        </Grid>

        <Typography variant="h5" fontWeight="semibold" mb={2}>
          Address
        </Typography>
        <Grid container spacing={2.5} mb={4} className='address-field'>
          <Grid item xs={12}>
            {window?.google && (
              <Box
                sx={{
                  '& .MuiOutlinedInput-root': { minHeight: 47, height: 47, alignItems: 'center' },
                  '& .MuiOutlinedInput-input': { py: '8.5px' },
                }}
              >
                <GoogleSuggestion
                  addressValue={formData.address?.address}
                  addressChangeHandler={(deliveryAddress) => {
                  // Debug: see exactly what we receive from Google selection
                  // eslint-disable-next-line no-console
                  setFormData((prev) => ({
                    ...prev,
                    address: {
                      address: deliveryAddress?.address || '',
                      city: deliveryAddress?.city || '',
                      state: deliveryAddress?.state || '',
                      zipCode: deliveryAddress?.zipcode || '',
                    },
                  }));
                  setAddressError(false);
                }}
                onClearAddress={() => {
                  setFormData((prev) => ({
                    ...prev,
                    address: {
                      address: '',
                      city: '',
                      state: '',
                      zipCode: '',
                    },
                  }));
                  setAddressError(false);
                }}
                googleAddressErrorHandler={(flag) => setAddressError(flag)}
                istrue="yes"
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="City"
              fullWidth
              // size="small"
              variant="outlined"
              name="address.city"
              value={formData.address?.city || ''}
              onChange={handleInputChange}
              disabled
              // sx={{
              //   '& .MuiOutlinedInput-root': { minHeight: 40, height: 40, '& .MuiOutlinedInput-input': { py: '8.5px' } },
              // }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="State"
              fullWidth
              // size="small"
              variant="outlined"
              name="address.state"
              value={
                (stateList || []).find((s) => s.stateCode === formData.address?.state)?.label ??
                formData.address?.state ??
                ''
              }
              onChange={handleInputChange}
              disabled
              // sx={{
              //   '& .MuiOutlinedInput-root': { minHeight: 40, height: 40, '& .MuiOutlinedInput-input': { py: '8.5px' } },
              // }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Postal Code"
              fullWidth
              // size="small"
              variant="outlined"
              name="address.zipCode"
              value={formData.address?.zipCode || ''}
              onChange={handleInputChange}
              disabled
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              // sx={{
              //   '& .MuiOutlinedInput-root': { minHeight: 40, height: 40, '& .MuiOutlinedInput-input': { py: '8.5px' } },
              // }}
            />
          </Grid>
        </Grid>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <MDButton
            sx={{ minWidth: '160px' }}
            type="button"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            loading={isLoading}
          >
            Save
          </MDButton>
          {distributorId && (
            <MDButton
              sx={{ minWidth: '160px', backgroundColor: '#F1F1F1', color: '#FD4438',backgroundColor: "#fcd3d0 !important" }}
              type="button"
              variant="contained"
              // color="error"
              
              onClick={() =>
                dispatch(
                  openCustomModalDialog({
                    modalData: {
                      deleteHandler: () => deleteHandler(distributorId),
                      deleteId: distributorId,
                      deleteType: 'Distributor',
                    },
                    modal: 'DELETE_MODAL',
                  })
                )
              }
            >
              Delete
            </MDButton>
          )}
          <MDButton
            sx={{ minWidth: '160px' }}
            type="button"
            variant="outlined"
            color="dark"
            onClick={() => navigate(BASE_PATH)}
          >
            Cancel
          </MDButton>
        </Stack>
      </Card>
    </Box>
  );
}
